import { createContext, useState, useEffect, useCallback } from "react";
import tokenService from "./tokenService";
import axiosRefresh from "../api/axiosRefresh"; // separate instance — see below

export const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    /* ── SILENT REFRESH ON APP LOAD ─────────────────────────────────
     * On page refresh, memory (_accessToken) is wiped.
     * But refresh_token is in localStorage.
     * So on every app boot, silently get a new access token
     * and decode user info from it — no /me call needed.           */

    useEffect(() => {
        const initAuth = async () => {
            const refreshToken = tokenService.getRefreshToken();

            if (!refreshToken) {
                setLoading(false);
                return;
            }

            try {

                const { data } = await axiosRefresh.post("/api/auth/refresh/", {
                    refresh: refreshToken,
                });

                const newAccessToken = data.access;
                tokenService.setAccessToken(newAccessToken);

                if (data.refresh) {
                    tokenService.setRefreshToken(data.refresh);
                }
                const userData = tokenService.getUserFromToken(newAccessToken);
                setUser(userData);
            } catch {
                tokenService.clearAll();
                setUser(null);
            } finally {
                setLoading(false);
            }
        };

        initAuth();
    }, []);


    const login = useCallback((access, refresh, email) => {
        tokenService.save(access, refresh);
        if (email) localStorage.setItem("adminEmail", email);

        const userData = tokenService.getUserFromToken(access);
        setUser(userData || { email });
    }, []);

    /* ── LOGOUT ──────────────────────────────────────────────────────
     * No /logout API → just clear everything locally.
     * Refresh token stays valid on server until expiry
     * (acceptable if backend has short refresh TTL).               */
    const logout = useCallback(() => {
        tokenService.clearAll();
        setUser(null);
    }, []);

    return (
        <AuthContext.Provider value={{ user, loading, login, logout }}>
            {children}
        </AuthContext.Provider>
    );
};